import { create } from "zustand";
import type {
	UserDetails,
	UserPreferences,
	UserProfile,
} from "@vita-learning/types";
import {
	destroyUser,
	updateProfile as updateUserProfile,
	updatePreferences as updateUserPreferences,
	setBetaStatus,
} from "~/shared/services";

export interface UserState {
	readonly data: UserDetails;
	readonly isPending: boolean;
}

interface UserActions {
	readonly updatePreferences: (
		preferences: UserPreferences
	) => Promise<UserDetails>;
	readonly updateProfile: (profile: UserProfile) => Promise<UserDetails>;
	readonly toggleBeta: () => Promise<UserDetails>;
	readonly destroy: () => Promise<void>;
	readonly reset: () => void;
}

export type UserStore = UserState & UserActions;

const defaultState: UserState = {
	data: {} as UserDetails,
	isPending: false,
};

export const createUserStore = (initialState: UserState) =>
	create<UserStore>()((set, get) => ({
		...defaultState,
		...initialState,
		updatePreferences: async preferences => {
			try {
				set({ isPending: true });
				const updatedUser = await updateUserPreferences(preferences);
				set({ data: updatedUser });
				return updatedUser;
			} finally {
				set({ isPending: false });
			}
		},
		updateProfile: async profile => {
			try {
				set({ isPending: true });
				const updatedUser = await updateUserProfile(profile);
				set({ data: updatedUser });
				return updatedUser;
			} finally {
				set({ isPending: false });
			}
		},
		toggleBeta: async () => {
			try {
				set({ isPending: true });

				const { data } = get();

				const updatedUser = {
					...data,
					flags: {
						...data.flags,
						beta: !data.flags.beta,
					},
				};

				await setBetaStatus(updatedUser.flags.beta);

				set({ data: updatedUser });
				return updatedUser;
			} finally {
				set({ isPending: false });
			}
		},
		destroy: async () => {
			try {
				set({ isPending: true });
				await destroyUser();
				set({ data: {} as UserDetails });
			} finally {
				set({ isPending: false });
			}
		},
		reset: () => set({ ...defaultState }),
	}));
